import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Resource } from "react-admin";
import AddIcon from "@material-ui/icons/Add";
import { useTokenOps } from "../components/TokenContext";
import { carbonRequest } from "./corporateCarbon/api";
import { carbonReferenceChoices } from "./corporateCarbon/referenceData";
import {
  CarbonAdminPage,
  CarbonDataTable,
  CarbonEditSurface,
  CarbonForm,
  CarbonFormToolbar,
  CarbonSection,
  CarbonSelectInput,
  CarbonStatus,
  CarbonTableActions,
  CarbonTableRow,
  CarbonTextInput,
  CarbonPageAction,
} from "./corporateCarbon/CarbonAdminPage";
import { carbonResourcePath, reportingRevisionLabel, reportingRevisionStatusLabel, withCarbonContext } from "./corporateCarbon/workflow";

const MAX_RESULTS = 100;
const emptyRevision = {
  reportingYear: "",
  reportingStartDate: "",
  reportingEndDate: "",
  baseYear: "",
  isBaseYearCorrection: false,
  baseYearRecalculationReason: "",
  consolidationApproachId: "",
  scope2Method: "",
  historicalProxyDeclared: false,
};
const friendlyErrors = {
  CARBON_REVISION_GRAPH_INCOMPLETE:
    "Revision is not ready to finalize. Complete its boundary and activity data, then try again.",
  CARBON_INVENTORY_YEAR_EXISTS:
    "An inventory already exists for this reporting year.",
  CARBON_INVENTORY_ARCHIVED:
    "This inventory is archived and cannot be changed.",
  CARBON_FEATURE_DISABLED:
    "Corporate carbon accounting is not enabled for this account.",
};
const messageFor = (error) =>
  friendlyErrors[error.code] || friendlyErrors[error.message] || error.message;
const titleCase = (value) =>
  value
    ? value.replace(/_/g, " ").replace(/^./, (char) => char.toUpperCase())
    : "Unknown";

const requireList = (value, label) => {
  if (!Array.isArray(value) || value.length > MAX_RESULTS)
    throw new Error(`Invalid carbon ${label} response.`);
  return value;
};

const revisionForm = (revision) => ({
  reportingStartDate: revision.reportingStartDate || "",
  reportingEndDate: revision.reportingEndDate || "",
  baseYear: revision.baseYear ?? "",
  isBaseYearCorrection: Boolean(revision.isBaseYearCorrection),
  baseYearRecalculationReason: revision.baseYearRecalculationReason || "",
  consolidationApproachId: revision.consolidationApproachId || "",
  scope2Method: revision.scope2Method || "",
  historicalProxyDeclared: Boolean(revision.historicalProxyDeclared),
});

const validateRevision = (form, includeYear) => {
  const year = Number(form.reportingYear);
  if (includeYear && (!Number.isInteger(year) || year < 1900 || year > 2200))
    return "Reporting year must be between 1900 and 2200.";
  if (!form.reportingStartDate || !form.reportingEndDate)
    return "Reporting start and end dates are required.";
  if (form.reportingEndDate < form.reportingStartDate)
    return "Reporting end date cannot be before its start date.";
  const start = Date.parse(`${form.reportingStartDate}T00:00:00.000Z`);
  const end = Date.parse(`${form.reportingEndDate}T00:00:00.000Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end))
    return "Reporting dates must be valid calendar dates.";
  if (end - start > 365 * 24 * 60 * 60 * 1000)
    return "Reporting period cannot exceed one year.";
  if (Number(form.reportingEndDate.slice(0, 4)) !== year)
    return "Reporting end date must be in the reporting year.";
  if (!form.consolidationApproachId)
    return "Consolidation approach is required.";
  if (!form.scope2Method) return "Scope 2 method is required.";
  if (
    form.baseYear &&
    (!Number.isInteger(Number(form.baseYear)) ||
      Number(form.baseYear) < 1900 ||
      Number(form.baseYear) > 2200)
  )
    return "Base year must be between 1900 and 2200.";
  if (form.baseYear && Number(form.baseYear) > year)
    return "Base year cannot be later than the reporting year.";
  if (form.isBaseYearCorrection && !form.baseYearRecalculationReason.trim())
    return "Base-year recalculation reason is required for a correction.";
  return null;
};

const payload = (form, includeYear) => {
  const body = {
    ...(includeYear ? { reportingYear: Number(form.reportingYear) } : {}),
    reportingStartDate: form.reportingStartDate,
    reportingEndDate: form.reportingEndDate,
    ...(includeYear && form.baseYear === ""
      ? {}
      : { baseYear: form.baseYear === "" ? null : Number(form.baseYear) }),
    isBaseYearCorrection: form.isBaseYearCorrection,
    ...(form.isBaseYearCorrection
      ? { baseYearRecalculationReason: form.baseYearRecalculationReason.trim() }
      : includeYear
        ? {}
        : { baseYearRecalculationReason: null }),
    consolidationApproachId: form.consolidationApproachId,
    scope2Method: form.scope2Method,
    historicalProxyDeclared: form.historicalProxyDeclared,
  };
  return body;
};

const RevisionFields = ({ form, setForm, approaches, prefix = "" }) => {
  const change = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  const label = (text) => (prefix ? `${prefix} ${text.toLowerCase()}` : text);
  return (
    <div>
      <CarbonTextInput label={label("Reporting start date")} name="reportingStartDate" type="date" value={form.reportingStartDate} onChange={change} InputLabelProps={{ shrink: true }} />
      <CarbonTextInput label={label("Reporting end date")} name="reportingEndDate" type="date" value={form.reportingEndDate} onChange={change} InputLabelProps={{ shrink: true }} />
      <CarbonTextInput label={label("Base year")} name="baseYear" type="number" value={form.baseYear} onChange={change} inputProps={{ min: 1900, max: 2200 }} />
      <CarbonSelectInput
        choices={form.consolidationApproachId && !approaches.some((item) => item.id === form.consolidationApproachId) ? [{ id: form.consolidationApproachId, name: `Current approach (${form.consolidationApproachId})` }, ...approaches] : approaches}
        emptyText="Select approach"
        label={label("Consolidation approach")}
        name="consolidationApproachId"
        value={form.consolidationApproachId}
        onChange={change}
      />
      <CarbonSelectInput choices={[{ id: "location_based", name: "Location based" }, { id: "market_based", name: "Market based" }, { id: "both", name: "Both" }]} emptyText="Select method" label={label("Scope 2 method")} name="scope2Method" value={form.scope2Method} onChange={change} />
      <FormControlLabel control={<Checkbox checked={form.isBaseYearCorrection} name="isBaseYearCorrection" onChange={change} />} label={label("Base-year correction")} />
      {form.isBaseYearCorrection && (
        <CarbonTextInput label={label("Base-year recalculation reason")} minRows={3} multiline name="baseYearRecalculationReason" value={form.baseYearRecalculationReason} onChange={change} />
      )}
      {["market_based", "both"].includes(form.scope2Method) && Number(form.baseYear) === Number(form.reportingYear) && (
        <>
          <FormControlLabel control={<Checkbox checked={form.historicalProxyDeclared} name="historicalProxyDeclared" onChange={change} />} label={label("Historical location-based proxy declared")} />
          <Typography color="textSecondary">Use the location-based factor as a warned market-based proxy for the base year only when no residual-mix factor is available.</Typography>
        </>
      )}
    </div>
  );
};

const RevisionRow = ({
  revision,
  reportingYear,
  approaches,
  inventoryActive,
  onSave,
  onAction,
  pending,
}) => {
  const initialData = Number(revision.revisionNumber) === 1;
  const [form, setForm] = useState(() => ({
    ...revisionForm(revision),
    reportingYear,
  }));
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  useEffect(
    () => setForm({ ...revisionForm(revision), reportingYear }),
    [reportingYear, revision],
  );
  const editable = inventoryActive && revision.status === "draft";
  const save = () => {
    const invalid = validateRevision(form, false);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError("");
    onSave(revision, payload(form, false));
  };
  return (
    <section
      aria-labelledby={`revision-${revision.id}`}
      style={{ borderTop: "1px solid #e0e0e0", marginTop: 16, paddingBlock: 20 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography id={`revision-${revision.id}`} variant="h6">
            {reportingRevisionStatusLabel(revision)}
          </Typography>
          <Typography color="textSecondary" variant="body2">
            {editable ? "Draft reporting data can still be changed." : `${initialData ? "This reporting data is" : "This revision is"} read-only and retained for auditability.`}
          </Typography>
          {!editable ? <Typography color="textSecondary" variant="caption">Read-only</Typography> : null}
        </Box>
        <CarbonStatus value={revision.status} label={titleCase(revision.status)} />
      </Box>
      {editable && editing ? (
        <>
          <Typography gutterBottom variant="subtitle1">{initialData ? "Reporting details" : "Revision details"}</Typography>
          <RevisionFields
            form={form}
            setForm={setForm}
            approaches={approaches}
            prefix={reportingRevisionLabel(revision)}
          />
          {error && (
            <Typography role="alert" color="error">
              {error}
            </Typography>
          )}
          <CarbonFormToolbar>
            <Button color="primary" onClick={save} disabled={pending} variant="contained">{initialData ? "Save reporting data" : "Save reporting revision"}</Button>
            <Button onClick={() => setEditing(false)} disabled={pending}>Cancel</Button>
          </CarbonFormToolbar>
        </>
      ) : (
        <>
        <Typography gutterBottom variant="subtitle1">{initialData ? "Reporting details" : "Revision details"}</Typography>
        <dl>
          <dt>Reporting period</dt>
          <dd>
            {revision.reportingStartDate} – {revision.reportingEndDate}
          </dd>
          <dt>Scope 2 method</dt>
          <dd>{titleCase(revision.scope2Method)}</dd>
          {revision.recalculationReason && <><dt>Reason for correction</dt><dd>{revision.recalculationReason}</dd></>}
        </dl>
        {editable && <Button color="primary" onClick={() => setEditing(true)} variant="outlined">Edit draft</Button>}
        </>
      )}
      {editable && (
        <Box borderTop="1px solid #e1e4e8" mt={3} pt={2}>
          <Typography gutterBottom variant="subtitle1">Reporting workflow</Typography>
          <Typography color="textSecondary" paragraph variant="body2">Complete boundaries, activity data, and evidence; finalize {initialData ? "the initial reporting data" : "this revision"}; then calculate emissions. Importing an Excel workbook can populate this draft or create a correction with a reason.</Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
          <CarbonPageAction to={withCarbonContext("/carbonBoundaries", { inventoryId: revision.inventoryId, revisionId: revision.id })} variant="outlined">
            Review boundary
          </CarbonPageAction>
          <CarbonPageAction to={withCarbonContext("/carbonActivities", { inventoryId: revision.inventoryId, revisionId: revision.id })} variant="outlined">
            Enter activity data
          </CarbonPageAction>
          <CarbonPageAction to={withCarbonContext("/carbonEvidence", { inventoryId: revision.inventoryId, revisionId: revision.id })} variant="outlined">Review evidence</CarbonPageAction>
          <CarbonPageAction color="primary" to={withCarbonContext("/carbonFinalization", { inventoryId: revision.inventoryId, revisionId: revision.id })} variant="outlined">
            Review finalization
          </CarbonPageAction>
          <CarbonPageAction to={withCarbonContext("/carbonCalculations", { inventoryId: revision.inventoryId, revisionId: revision.id })} variant="outlined">
            Calculate emissions
          </CarbonPageAction>
          </Box>
          <Box borderTop="1px solid #e1e4e8" mt={2} pt={2}>
            <Typography gutterBottom variant="subtitle1">{initialData ? "Reporting lifecycle" : "Revision lifecycle"}</Typography>
            <Button color="primary" disabled={pending} onClick={() => onAction("finalize", revision)} variant="contained">
              {initialData ? "Finalize initial reporting data" : "Finalize reporting revision"}
            </Button>
          </Box>
        </Box>
      )}
      {inventoryActive && revision.status === "finalized" && (
        <Box borderTop="1px solid #e1e4e8" mt={3} pt={2}>
          <Typography gutterBottom variant="subtitle1">{initialData ? "Reporting lifecycle" : "Revision lifecycle"}</Typography>
          <Button disabled={pending} onClick={() => onAction("correction", revision)} variant="outlined">
            Create correction from {reportingRevisionLabel(revision)}
          </Button>
        </Box>
      )}
    </section>
  );
};

export const CarbonInventoryPage = ({
  accessToken,
  createOnly = false,
  showInventoryId = "",
  showOnly = false,
}) => {
  const tokenOps = useTokenOps();
  const currentAccessToken = accessToken || tokenOps.account?.accessToken;
  const tokenRef = useRef(currentAccessToken);
  tokenRef.current = currentAccessToken;
  const [inventories, setInventories] = useState([]);
  const [approaches, setApproaches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [creating, setCreating] = useState(createOnly);
  const [createForm, setCreateForm] = useState(emptyRevision);
  const [confirmation, setConfirmation] = useState(null);
  const [correctionReason, setCorrectionReason] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const [reload, setReload] = useState(0);
  const generationRef = useRef(0);
  const historyGenerationRef = useRef(0);
  const selectedIdRef = useRef(null);

  const loadInventories = async (token) =>
    requireList(
      await carbonRequest(token, "corporate-carbon/inventories"),
      "inventory",
    );
  useEffect(() => {
    const generation = ++generationRef.current;
    let active = true;
    setLoading(true);
    setLoadFailed(false);
    setError("");
    setSuccess("");
    setInventories([]);
    setApproaches([]);
    setSelected(null);
    selectedIdRef.current = null;
    historyGenerationRef.current += 1;
    setRevisions([]);
    setCreating(createOnly);
    setCreateForm(emptyRevision);
    setConfirmation(null);
    setPending(false);
    pendingRef.current = false;
    if (!currentAccessToken) {
      setLoading(false);
      setError("An active account token is required.");
      return () => {
        active = false;
      };
    }
    Promise.all([
      loadInventories(currentAccessToken),
      carbonReferenceChoices(currentAccessToken, "consolidationApproach"),
    ])
      .then(([loaded, choices]) => {
        if (active && generationRef.current === generation) {
          setInventories(loaded);
          setApproaches(choices);
        }
      })
      .catch((requestError) => {
        if (active && generationRef.current === generation) {
          setLoadFailed(true);
          setError(messageFor(requestError));
        }
      })
      .finally(() => {
        if (active && generationRef.current === generation) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [createOnly, currentAccessToken, reload]);

  const operation = () => ({
    token: currentAccessToken,
    generation: generationRef.current,
  });
  const isCurrent = ({ token, generation }) =>
    tokenRef.current === token && generationRef.current === generation;

  const openRevisions = async (item) => {
    const requestToken = currentAccessToken;
    const historyGeneration = ++historyGenerationRef.current;
    selectedIdRef.current = item.id;
    setSelected(item);
    setRevisions([]);
    setLoadingRevisions(true);
    setError("");
    try {
      const loaded = requireList(
        await carbonRequest(
          requestToken,
          `corporate-carbon/inventories/${item.id}/revisions`,
        ),
        "revision",
      );
      if (
        tokenRef.current === requestToken &&
        historyGenerationRef.current === historyGeneration &&
        selectedIdRef.current === item.id
      )
        setRevisions(loaded);
    } catch (requestError) {
      if (
        tokenRef.current === requestToken &&
        historyGenerationRef.current === historyGeneration &&
        selectedIdRef.current === item.id
      )
        setError(messageFor(requestError));
    } finally {
      if (
        tokenRef.current === requestToken &&
        historyGenerationRef.current === historyGeneration &&
        selectedIdRef.current === item.id
      )
        setLoadingRevisions(false);
    }
  };
  useEffect(() => {
    if (!showInventoryId || loading || selected?.id === showInventoryId) return;
    const inventory = inventories.find((item) => item.id === showInventoryId);
    if (inventory) openRevisions(inventory);
    else if (!loadFailed) setError("Inventory not found.");
  // openRevisions deliberately uses the current token and generation refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventories, loadFailed, loading, selected, showInventoryId]);
  const refresh = async (request, selectedInventory = selected) => {
    const loaded = await loadInventories(request.token);
    if (!isCurrent(request)) return;
    setInventories(loaded);
    if (selectedInventory) {
      const updated =
        loaded.find((item) => item.id === selectedInventory.id) ||
        selectedInventory;
      setSelected(updated);
      selectedIdRef.current = updated.id;
      const historyGeneration = ++historyGenerationRef.current;
      const loadedRevisions = requireList(
        await carbonRequest(
          request.token,
          `corporate-carbon/inventories/${updated.id}/revisions`,
        ),
        "revision",
      );
      if (
        isCurrent(request) &&
        historyGenerationRef.current === historyGeneration &&
        selectedIdRef.current === updated.id
      )
        setRevisions(loadedRevisions);
    }
  };
  const create = async (event) => {
    event.preventDefault();
    const invalid = validateRevision(createForm, true);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError("");
    setSuccess("");
    if (pendingRef.current) return;
    const request = operation();
    setPending(true);
    pendingRef.current = true;
    try {
      await carbonRequest(request.token, "corporate-carbon/inventories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload(createForm, true)),
      });
      if (!isCurrent(request)) return;
      setSuccess("Inventory created.");
      setCreating(false);
      setCreateForm(emptyRevision);
      await refresh(request, null);
    } catch (requestError) {
      if (isCurrent(request)) setError(messageFor(requestError));
    } finally {
      if (isCurrent(request)) {
        pendingRef.current = false;
        setPending(false);
      }
    }
  };
  const saveRevision = async (revision, body) => {
    setError("");
    setSuccess("");
    if (pendingRef.current) return;
    const request = operation();
    setPending(true);
    pendingRef.current = true;
    try {
      await carbonRequest(
        request.token,
        `corporate-carbon/revisions/${revision.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!isCurrent(request)) return;
      setSuccess("Draft revision saved.");
      await refresh(request);
    } catch (requestError) {
      if (isCurrent(request)) setError(messageFor(requestError));
    } finally {
      if (isCurrent(request)) {
        pendingRef.current = false;
        setPending(false);
      }
    }
  };
  const ask = (kind, target) => { setCorrectionReason(""); setConfirmation({ kind, target }); };
  const confirm = async () => {
    const action = confirmation;
    if (!action || pendingRef.current) return;
    const request = operation();
    setError("");
    setSuccess("");
    setPending(true);
    pendingRef.current = true;
    let successMessage = "";
    try {
      if (action.kind === "finalize") {
        await carbonRequest(
          request.token,
          `corporate-carbon/revisions/${action.target.id}/finalize`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: "{}",
          },
        );
        successMessage = "Revision finalized.";
      }
      if (action.kind === "correction") {
        await carbonRequest(
          request.token,
          `corporate-carbon/revisions/${action.target.id}/corrections`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ supersedesRevisionId: action.target.id, recalculationReason: correctionReason.trim() }),
          },
        );
        successMessage = "Correction draft created.";
      }
      if (action.kind === "archive") {
        await carbonRequest(
          request.token,
          `corporate-carbon/inventories/${action.target.id}/archive`,
          { method: "POST" },
        );
        successMessage = "Inventory archived.";
      }
      if (!isCurrent(request)) return;
      setConfirmation(null);
      setSuccess(successMessage);
      await refresh(request);
    } catch (requestError) {
      if (isCurrent(request)) {
        setError(messageFor(requestError));
      }
    } finally {
      if (isCurrent(request)) {
        pendingRef.current = false;
        setPending(false);
      }
    }
  };
  const confirmationTitle =
    confirmation?.kind === "finalize"
      ? `Finalize ${reportingRevisionLabel(confirmation.target)}?`
      : confirmation?.kind === "correction"
        ? `Create correction from ${reportingRevisionLabel(confirmation.target)}?`
        : confirmation
          ? `Archive inventory ${confirmation.target.reportingYear}?`
          : "Confirm action";

  return (
    <CarbonAdminPage
      title={showOnly && selected ? `Inventory ${selected.reportingYear}` : "Carbon inventories"}
      description={showOnly ? "Inventory details, reporting revisions, and the workflow for the selected reporting year." : "Reporting years and immutable revision history for the active account."}
      actions={!showOnly && !createOnly && !loading && !loadFailed && !creating ? (
        <Button
          variant="contained"
          color="primary"
          disabled={pending}
          onClick={() => setCreating(true)}
        >
          Create inventory
        </Button>
      ) : null}
    >
        {loading && (
          <div role="status">
            <CircularProgress size={20} aria-hidden="true" /> Loading
            inventories…
          </div>
        )}
        {!loading && error && (
          <Typography role="alert" color="error">
            {error}
          </Typography>
        )}
        {success && (
          <Typography role="status" aria-live="polite">
            {success}
          </Typography>
        )}
        {!loading && loadFailed && (
          <Button onClick={() => setReload((value) => value + 1)}>
            Retry loading inventories
          </Button>
        )}
        <CarbonEditSurface
          dedicated={createOnly}
          description="Create the reporting year and its initial reporting revision."
          onClose={() => { if (!pending) setCreating(false); }}
          open={creating}
          title="Create inventory"
        >
          {error ? <Typography role="alert" color="error">{error}</Typography> : null}
          <CarbonForm aria-label="Inventory form" onSubmit={create}>
            <CarbonTextInput
              label="Reporting year"
              name="reportingYear"
              type="number"
              value={createForm.reportingYear}
              onChange={(event) => setCreateForm((current) => ({ ...current, reportingYear: event.target.value }))}
              inputProps={{ min: 1900, max: 2200 }}
            />
            <RevisionFields
              form={createForm}
              setForm={setCreateForm}
              approaches={approaches}
            />
            <CarbonFormToolbar>
            <Button color="primary" type="submit" disabled={pending} variant="contained">
              {pending ? "Saving…" : "Save new inventory"}
            </Button>
              <Button onClick={() => setCreating(false)}>Cancel</Button>
            </CarbonFormToolbar>
          </CarbonForm>
        </CarbonEditSurface>
        {!showOnly && !createOnly && !loading && !loadFailed && inventories.length === 0 && (
          <Typography>No inventories exist for this account.</Typography>
        )}
        {!showOnly && !createOnly && !loading && !loadFailed && inventories.length > 0 && (
          <CarbonSection
            title="Inventory history"
            description="Select an inventory to review its immutable revision history."
          >
          <CarbonDataTable label="Carbon inventories">
            <TableHead>
              <TableRow>
                <TableCell>Reporting year</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventories.map((item) => (
                <CarbonTableRow
                  ariaLabel={`Inventory ${item.reportingYear}; select to view revisions`}
                  key={item.id}
                  onSelect={() => openRevisions(item)}
                  selected={selected?.id === item.id}
                >
                  <TableCell>{item.reportingYear}</TableCell>
                  <TableCell><CarbonStatus value={item.status} label={titleCase(item.status)} /></TableCell>
                  <TableCell align="right">
                    <CarbonTableActions label={`Actions for inventory ${item.reportingYear}`}>
                    <Button
                      aria-label={`View revisions for ${item.reportingYear}`}
                      disabled={pending}
                      onClick={() => openRevisions(item)}
                    >
                      View revisions
                    </Button>
                    {item.status === "active" && (
                      <Button
                        aria-label={`Archive inventory ${item.reportingYear}`}
                        disabled={pending}
                        onClick={() => ask("archive", item)}
                      >
                        Archive
                      </Button>
                    )}
                    </CarbonTableActions>
                  </TableCell>
                </CarbonTableRow>
              ))}
            </TableBody>
          </CarbonDataTable>
          </CarbonSection>
        )}
        {showOnly && selected && (
          <CarbonSection title="Inventory summary" description="The reporting-year record that owns the revision history below.">
            <dl>
              <dt>Reporting year</dt><dd>{selected.reportingYear}</dd>
              <dt>Status</dt><dd><CarbonStatus value={selected.status} label={titleCase(selected.status)} /></dd>
              <dt>Reporting records</dt><dd>{revisions.length}</dd>
            </dl>
          </CarbonSection>
        )}
        {selected && (
          <CarbonSection
            title={`Reporting history for ${selected.reportingYear}`}
            description="Draft reporting data can be edited. Finalized data and superseded corrections remain read-only for auditability."
          >
            {loadingRevisions && (
              <div role="status">Loading revision history…</div>
            )}
            {!loadingRevisions && revisions.length === 0 && (
              <Typography>No revisions found.</Typography>
            )}
            {revisions.map((item) => (
              <RevisionRow
                key={item.id}
                revision={item}
                reportingYear={selected.reportingYear}
                approaches={approaches}
                inventoryActive={selected.status === "active"}
                onSave={saveRevision}
                onAction={ask}
                pending={pending}
              />
            ))}
          </CarbonSection>
        )}
        <Dialog
          open={Boolean(confirmation)}
          onClose={pending ? undefined : () => setConfirmation(null)}
          aria-labelledby="carbon-confirm-title"
          BackdropProps={process.env.NODE_ENV === "test" ? { transitionDuration: 0 } : undefined}
          transitionDuration={process.env.NODE_ENV === "test" ? 0 : undefined}
        >
          <DialogTitle id="carbon-confirm-title">
            {confirmationTitle}
          </DialogTitle>
          <DialogContent>
            {confirmation?.kind === "correction" ? <CarbonTextInput label="Reason for correction" name="recalculationReason" required value={correctionReason} onChange={(event) => setCorrectionReason(event.target.value)} /> : null}
            <Typography>
               {confirmation?.kind === "archive"
                 ? "The inventory will no longer be editable or active. All reporting revisions, calculations, evidence, and audit history will remain preserved."
                 : "This lifecycle action changes the inventory history. Review the selected record before continuing."}
            </Typography>
            {error && (
              <Typography role="alert" color="error">
                {error}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={pending} onClick={() => setConfirmation(null)}>
              Cancel
            </Button>
            <Button
              disabled={pending || (confirmation?.kind === "correction" && !correctionReason.trim())}
              onClick={confirm}
              color="primary"
              variant="contained"
            >
              {pending ? "Working…" : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
    </CarbonAdminPage>
  );
};

export const CarbonInventoryList = ({ accessToken, history }) => {
  const tokenOps = useTokenOps();
  const token = accessToken || tokenOps.account?.accessToken;
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    carbonRequest(token, "corporate-carbon/inventories")
      .then((rows) => {
        if (active) setInventories(requireList(rows, "inventory"));
      })
      .catch((requestError) => {
        if (active) setError(messageFor(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [token]);

  const show = (id) => history.push(carbonResourcePath("carbonInventories", id));
  return (
    <CarbonAdminPage
      title="Carbon inventories"
      description="Reporting years and immutable revision history for the active account."
      actions={<CarbonPageAction color="primary" startIcon={<AddIcon />} to={carbonResourcePath("carbonInventories", null, "create")}>Create inventory</CarbonPageAction>}
      variant="list"
    >
      {loading ? <div role="status">Loading inventories…</div> : null}
      {error ? <Typography color="error" role="alert">{error}</Typography> : null}
      {!loading && !error && inventories.length === 0 ? <Typography>No inventories exist for this account.</Typography> : null}
      {!loading && !error && inventories.length > 0 ? (
        <CarbonSection title="Inventory history" description="Open an inventory to review its reporting revisions.">
          <CarbonDataTable label="Carbon inventories">
            <TableHead><TableRow><TableCell>Reporting year</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
            <TableBody>{inventories.map((item) => (
              <CarbonTableRow ariaLabel={`View inventory ${item.reportingYear}`} key={item.id} onSelect={() => show(item.id)}>
                <TableCell>{item.reportingYear}</TableCell>
                <TableCell><CarbonStatus value={item.status} label={titleCase(item.status)} /></TableCell>
              </CarbonTableRow>
            ))}</TableBody>
          </CarbonDataTable>
        </CarbonSection>
      ) : null}
    </CarbonAdminPage>
  );
};

export const CarbonInventoryShow = (props) => (
  <CarbonInventoryPage {...props} showInventoryId={props.id} showOnly />
);

export const CarbonInventoryCreate = (props) => (
  <CarbonInventoryPage {...props} createOnly />
);

const carbonInventoryResource = () => (
  <Resource
    name="carbonInventories"
    options={{ label: "Inventories", menuGroup: "Carbon Accounting" }}
    list={CarbonInventoryList}
    show={CarbonInventoryShow}
    create={CarbonInventoryCreate}
  />
);
export default carbonInventoryResource;
